'use client';

import type { ListProduct } from '@/api/types/public';
import { listByProductIds } from '@/api/public';
import type { CompleteTrackingParams } from '@/app/(home-page)/types';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import ProductCard from '@/components/Product/ProductCard';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { serializeKey } from '@/utils/swr/keys';
import { RightArrowFineOutlined } from '@zeelool/icon';
import { useCallback, useId, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import useSWR from 'swr';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { ProductInspirationShowcase2026Props } from './types';

const MIN_PRODUCT_COUNT = 2;
const MAX_PRODUCT_COUNT = 6;
const SKELETON_ITEMS = ['product-skeleton-1', 'product-skeleton-2'];
const PRODUCT_IMAGE_HOVER_RULES = [EProductImageType.PRODUCT];
const AUTOPLAY_DELAY = 3000;
const PRODUCT_SLIDE_CLASS_NAME =
  '!mr-[8px] !w-[208px] last:!mr-0 xl:!w-[calc(15.625vw+40px)] 3xl:!w-[340px]';

const normalizeConfiguredText = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.replace(/<br\s*\/?>/gi, '\n').trim();
};

const getConfiguredProductId = (item: unknown) => {
  if (!item || typeof item !== 'object' || !('sku' in item)) return null;

  const sku = item.sku;
  if (!sku || typeof sku !== 'object') return null;

  const id = 'id' in sku ? sku.id : undefined;
  const productId = Number(id);
  return Number.isFinite(productId) && productId > 0 ? productId : null;
};

const getProductInfo = (productInfo: unknown): ListProduct | null => {
  if (!productInfo || typeof productInfo !== 'object') return null;

  const id = 'id' in productInfo ? productInfo.id : undefined;
  const shopSku = 'shop_sku' in productInfo ? productInfo.shop_sku : undefined;
  if (!id || typeof shopSku !== 'string' || !shopSku.trim()) return null;

  return productInfo as unknown as ListProduct;
};

const getProductCardKey = (product: ListProduct, productIndex: number, currencyName: string) =>
  `${String(product.id || product.shop_sku || productIndex)}-${currencyName}-${JSON.stringify(product)}`;

const getTrackingProductFields = (product: ListProduct) => {
  const extendedProduct = product as ListProduct & {
    business_sku?: string | number;
    sku?: string | number;
  };

  return {
    businessSku: String(extendedProduct.business_sku || ''),
    productSku: String(extendedProduct.sku || ''),
  };
};

const buildProductTrackingParams = ({
  product,
  productIndex,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
}: {
  product: ListProduct;
  productIndex: number;
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
}): CompleteTrackingParams => {
  const { businessSku, productSku } = getTrackingProductFields(product);

  return {
    module_index: moduleIndex,
    module_name: componentId,
    module_id: moduleId,
    module_title: moduleTitle,
    mkt_index: productIndex,
    mkt_index_sub: '',
    mkt_jump_type: '2',
    mkt_jump_content: String(product.shop_sku || product.id || ''),
    shop_sku: String(product.shop_sku || ''),
    business_sku: businessSku,
    product_sku: productSku,
    product_id: String(product.id || ''),
    product_name: String(product.short_name || ''),
  };
};

function ProductCardSkeleton() {
  return (
    <div className="flex w-full animate-pulse flex-col gap-[12px]" aria-hidden>
      <div className="aspect-[470/588] w-full bg-primary-white" />
      <div className="flex flex-col gap-[8px] px-[8px]">
        <div className="h-[17px] w-3/5 rounded bg-primary-white" />
        <div className="h-[19px] w-2/5 rounded bg-primary-white" />
        <div className="h-[22px] w-1/2 rounded bg-primary-white" />
      </div>
    </div>
  );
}

export default function ProductInspirationShowcase2026({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: ProductInspirationShowcase2026Props) {
  const currency = useStore((state) => state.currency);
  const token = useStore((state) => state.token);
  const reactId = useId();
  const navigationId = useMemo(
    () => `product-inspiration-${reactId.replace(/[^a-zA-Z0-9-]/g, '-')}`,
    [reactId],
  );
  const exposedProductIds = useRef(new Set<string>());
  const validExposedProductIds = useRef(new Set<string>());
  const { ref: inViewRef, inView } = useInView({ threshold: 0, triggerOnce: true });

  const configuredProductIds = useMemo(() => {
    if (!Array.isArray(indexData.product_list)) return [];
    return indexData.product_list
      .map(getConfiguredProductId)
      .filter((productId): productId is number => typeof productId === 'number')
      .slice(0, MAX_PRODUCT_COUNT);
  }, [indexData.product_list]);
  const operationImage = normalizeConfiguredText(indexData.operation_image);
  const hasValidConfiguration =
    configuredProductIds.length >= MIN_PRODUCT_COUNT && Boolean(operationImage);
  const currencyName = normalizeConfiguredText(currency?.name);
  const configuredProductIdsKey = configuredProductIds.join(',');
  const fixedSkuRequestKey =
    hasValidConfiguration && inView
      ? serializeKey([
          'productInspirationShowcase2026',
          'fixedSkuProducts',
          {
            productIds: configuredProductIdsKey,
            currency: currencyName,
            isLoggedIn: Boolean(token),
          },
        ])
      : null;
  const {
    data: fixedSkuProductsData,
    error: fixedSkuProductsError,
    isLoading,
  } = useSWR<unknown[] | null>(
    fixedSkuRequestKey,
    async () => {
      const response = await listByProductIds({
        product_ids: configuredProductIdsKey,
        currency: currencyName,
      });

      if (response?.code !== 200 || !Array.isArray(response.data?.list)) return null;
      return response.data.list;
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 300000,
      keepPreviousData: true,
    },
  );
  const hasAttemptedFetch = fixedSkuProductsData !== undefined || Boolean(fixedSkuProductsError);

  const products = useMemo(() => {
    if (!Array.isArray(fixedSkuProductsData)) return [];

    const productsById = new Map<number, ListProduct>();
    fixedSkuProductsData.forEach((entry) => {
      const product = getProductInfo(entry);
      const productId = Number(product?.id);
      if (product && Number.isFinite(productId)) productsById.set(productId, product);
    });

    return configuredProductIds
      .map((productId) => productsById.get(productId))
      .filter((product): product is ListProduct => Boolean(product));
  }, [configuredProductIds, fixedSkuProductsData]);

  const headingTitle = normalizeConfiguredText(indexData.layout_title);
  const headingSubtitle = normalizeConfiguredText(indexData.layout_subtitle);
  const shouldShowHeading =
    Number(indexData.layout_title_status) === 1 && Boolean(headingTitle || headingSubtitle);
  const headingTitleColor = normalizeConfiguredText(indexData.layout_title_color);
  const headingSubtitleColor =
    normalizeConfiguredText(indexData.layout_subtitle_color) ||
    normalizeConfiguredText(indexData.layout_subtitl_color);
  const componentId = indexData.component_id;
  const moduleId = String(indexData.blocks_advances_id || indexData.id || '');

  const buildTrackingParams = useCallback(
    (product: ListProduct, productIndex: number) =>
      buildProductTrackingParams({
        product,
        productIndex,
        moduleIndex,
        componentId,
        moduleId,
        moduleTitle: headingTitle,
      }),
    [componentId, headingTitle, moduleId, moduleIndex],
  );

  const handleProductClick = useCallback(
    (product: ListProduct, productIndex: number) => {
      if (!onTrack) return;

      try {
        onTrack(buildTrackingParams(product, productIndex));
      } catch (error) {
        logger.error('2026 PC product inspiration click tracking error:', error);
      }
    },
    [buildTrackingParams, onTrack],
  );

  const handleProductExposure = useCallback(
    (product: ListProduct, productIndex: number) => {
      const productKey = `${String(product.id || product.shop_sku || productIndex)}-${moduleIndex}`;
      const trackingParams = buildTrackingParams(product, productIndex);

      try {
        if (onExpTrack && !exposedProductIds.current.has(productKey)) {
          onExpTrack(trackingParams);
          exposedProductIds.current.add(productKey);
        }
        if (onValidExpTrack && !validExposedProductIds.current.has(productKey)) {
          onValidExpTrack(trackingParams);
          validExposedProductIds.current.add(productKey);
        }
      } catch (error) {
        logger.error('2026 PC product inspiration exposure tracking error:', error);
      }
    },
    [buildTrackingParams, moduleIndex, onExpTrack, onValidExpTrack],
  );

  if (!hasValidConfiguration) return null;
  if (hasAttemptedFetch && products.length < MIN_PRODUCT_COUNT) return null;

  const hasProducts = !isLoading && products.length >= MIN_PRODUCT_COUNT;

  return (
    <section ref={inViewRef} data-component-id={componentId} className="w-full bg-primary-white">
      {shouldShowHeading ? (
        <header className="flex w-full flex-col items-center justify-center gap-[8px] px-[16px] py-[24px] text-center">
          {headingTitle ? (
            <h2
              className="w-full whitespace-pre-line font-bb text-[32px] leading-[39px]"
              style={headingTitleColor ? { color: headingTitleColor } : undefined}
            >
              {headingTitle}
            </h2>
          ) : null}
          {headingSubtitle ? (
            <p
              className="whitespace-pre-line font-br text-[16px] leading-[19px] 3xl:text-[20px] 3xl:leading-[24px]"
              style={headingSubtitleColor ? { color: headingSubtitleColor } : undefined}
            >
              {headingSubtitle}
            </p>
          ) : null}
        </header>
      ) : null}

      <div className="grid w-full grid-cols-2 bg-auxiliary-light-gray">
        <div className="relative flex min-w-0 items-center justify-center overflow-hidden">
          <div className="w-[208px] xl:w-[calc(31.25vw+88px)] 3xl:w-[688px]">
            <Swiper
              key={hasProducts ? 'products' : 'skeleton'}
              modules={[Autoplay, Navigation]}
              slidesPerView="auto"
              spaceBetween={0}
              loop={hasProducts}
              autoplay={
                hasProducts
                  ? {
                      delay: AUTOPLAY_DELAY,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }
                  : false
              }
              observer
              observeParents
              navigation={{
                prevEl: `.${navigationId}-prev`,
                nextEl: `.${navigationId}-next`,
              }}
              className="!overflow-hidden"
            >
              {hasProducts
                ? products.map((product, productIndex) => (
                    <SwiperSlide
                      key={getProductCardKey(product, productIndex, currencyName)}
                      className={PRODUCT_SLIDE_CLASS_NAME}
                    >
                      <ProductCard
                        product={product}
                        index={productIndex}
                        showCorrelation
                        showReviewStar
                        showTryOn={false}
                        showMember={false}
                        showTag={false}
                        showOriginalPrice
                        showName={false}
                        namePriceInline={false}
                        imageClassName="!aspect-[470/588] !rounded-none"
                        productInfoClassName="!px-[8px] !pt-[8px]"
                        imageHoverRule={PRODUCT_IMAGE_HOVER_RULES}
                        hoverProductImagePriority="white-second-gray-first"
                        productImageWhiteBg
                        handelClick={(cardIndex, clickedProduct) => {
                          handleProductClick(clickedProduct as ListProduct, cardIndex);
                        }}
                        onValidExposure={(cardIndex, exposedProduct) => {
                          handleProductExposure(exposedProduct as ListProduct, cardIndex);
                        }}
                      />
                    </SwiperSlide>
                  ))
                : SKELETON_ITEMS.map((item) => (
                    <SwiperSlide key={item} className={PRODUCT_SLIDE_CLASS_NAME}>
                      <ProductCardSkeleton />
                    </SwiperSlide>
                  ))}
            </Swiper>
          </div>

          <button
            type="button"
            aria-label="Previous products"
            className={`${navigationId}-prev absolute left-[104px] top-1/2 z-10 flex size-[32px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 text-primary-white xl:left-[28px] 3xl:left-[48px] [&.swiper-button-disabled]:hidden`}
          >
            <span className="flex size-[20px] rotate-180 items-center justify-center">
              <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
            </span>
          </button>
          <button
            type="button"
            aria-label="Next products"
            className={`${navigationId}-next absolute right-[104px] top-1/2 z-10 flex size-[32px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 text-primary-white xl:right-[28px] 3xl:right-[48px] [&.swiper-button-disabled]:hidden`}
          >
            <span className="flex size-[20px] items-center justify-center">
              <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
            </span>
          </button>
        </div>

        <MagicJump
          item={indexData.operation}
          prefetch
          aria-label={headingTitle || headingSubtitle || undefined}
          className="relative block aspect-[5/4] w-full overflow-hidden"
        >
          <Image
            src={operationImage}
            alt=""
            fill
            priority={moduleIndex < 3}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            sizes="50vw"
            className="object-cover"
          />
        </MagicJump>
      </div>
    </section>
  );
}
